import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { appStrings } from '@backrs_ui/common-ui/src/assets/appStrings';
import { COLOR_TOKENS } from '@backrs_ui/common-ui/src/designSystem/colors/colors';
import { formatCurrency } from '@backrs_ui/common-ui/src/utils/stringFormatters.util';
import { Box } from '@mui/material';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, } from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import { Line } from 'react-chartjs-2';
import seedrandom from 'seedrandom';
import { ROILineChartHeader } from './ROILineChartHeader';
var ROI_LINE_CHART_STRINGS = appStrings.modules.charts.returnOnInvestment;
function makeSquigglyLine(data, resolution, amplitude, schoolId) {
    var rng = seedrandom(schoolId.toString());
    return data
        .slice(0, -1)
        .flatMap(function (start, i) {
        var end = data[i + 1];
        return Array.from({ length: resolution }, function (_, j) {
            var t = j / resolution;
            var linear = (1 - t) * start + t * end;
            var freqMultiplier = 4 + Math.floor(rng() * 6);
            var ampMultiplier = 0.7 + rng() * 1.0;
            var phaseOffset = rng() * Math.PI * 2;
            var wiggle = Math.sin(t * Math.PI * freqMultiplier + phaseOffset) *
                amplitude *
                ampMultiplier *
                (end - start);
            return linear + wiggle;
        });
    })
        .concat(data[data.length - 1]);
}
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, annotationPlugin);
export var ROILineChart = function (_a) {
    var actualData = _a.actualData, title = _a.title, total_roi = _a.total_roi, schoolId = _a.schoolId;
    var resolution = 2.5;
    var howTallOfSquiggles = 0.25;
    var squigglyData = makeSquigglyLine(actualData, resolution, howTallOfSquiggles, schoolId);
    var squigglyLabels = Array.from({ length: squigglyData.length }, function (_, i) {
        return (i / resolution).toFixed(1);
    });
    var data = {
        labels: squigglyLabels,
        datasets: [
            {
                label: title,
                data: squigglyData,
                borderColor: COLOR_TOKENS['Brand/500'],
                tension: 0.25,
                pointRadius: 0,
                pointHoverRadius: 0,
            },
        ],
    };
    var lowestTick = Math.min.apply(Math, actualData);
    var highestTick = Math.max.apply(Math, actualData);
    var options = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            y: {
                title: {
                    display: true,
                    text: ROI_LINE_CHART_STRINGS.yAxisLabel,
                    color: COLOR_TOKENS['Colors/Text/text-tertiary'],
                    font: {
                        size: 12,
                        family: 'Sarabun, sans-serif',
                    },
                },
                grid: {
                    tickColor: 'transparent',
                    display: true,
                    color: function (context) {
                        var value = context.tick.value;
                        if (value === 0) {
                            return COLOR_TOKENS['Colors/Foreground/fg-tertiary'];
                        }
                        return COLOR_TOKENS['Colors/Border/border-tertiary'];
                    },
                },
                border: {
                    display: false,
                },
                min: Math.min(lowestTick - 100000, -250000),
                max: Math.max(highestTick + 50000, 500000),
                afterBuildTicks: function (axis) {
                    var minTick = Math.min(Math.floor(lowestTick / 250000) * 250000, -250000);
                    var maxTick = Math.max(Math.ceil(highestTick / 250000) * 250000, 1000000);
                    var tickCount = (maxTick - minTick) / 250000 + 1;
                    axis.ticks = Array.from({ length: tickCount }, function (_, i) { return ({
                        value: minTick + i * 250000,
                    }); });
                },
                ticks: {
                    callback: function (value) {
                        var valueAsNumber = Number(value);
                        if (valueAsNumber % 250000 === 0) {
                            var absValue = Math.abs(valueAsNumber);
                            var suffix = absValue >= 1000000 ? 'M' : 'K';
                            var formattedValue = absValue >= 1000000 ? absValue / 1000000 : absValue / 1000;
                            return "".concat(valueAsNumber < 0 ? '-' : '', "$").concat(formattedValue).concat(suffix, " ");
                        }
                        return '';
                    },
                    font: {
                        size: 12,
                        weight: 'lighter',
                        family: 'Sarabun, sans-serif',
                    },
                },
            },
            x: {
                title: {
                    display: true,
                    text: ROI_LINE_CHART_STRINGS.xAxisLabel,
                    color: COLOR_TOKENS['Colors/Text/text-tertiary'],
                    padding: {
                        top: 16,
                        bottom: 0,
                    },
                    font: {
                        size: 12,
                        family: 'Sarabun, sans-serif',
                    },
                },
                grid: {
                    display: false,
                },
                border: {
                    display: false,
                },
                ticks: {
                    align: 'end',
                    font: {
                        size: 12,
                        weight: 'lighter',
                        family: 'Sarabun, sans-serif',
                    },
                    autoSkip: false,
                    maxTicksLimit: squigglyLabels.length,
                    minRotation: 0,
                    maxRotation: 0,
                    callback: function (_value, index) {
                        var label = Number(squigglyLabels[index]);
                        if (Number.isInteger(label) && label % 2 === 0 && label !== 0) {
                            return label.toString();
                        }
                        return '';
                    },
                },
            },
        },
        interaction: {
            mode: 'nearest',
            axis: 'x',
            intersect: false,
        },
        plugins: {
            tooltip: {
                enabled: true,
                displayColors: false,
                padding: {
                    top: 8,
                    bottom: 8,
                    left: 12,
                    right: 12,
                },
                caretSize: 0,
                yAlign: 'bottom',
                callbacks: {
                    title: function (context) {
                        var hoveredIndex = context[0].dataIndex;
                        var year = Math.round(hoveredIndex / resolution);
                        return "".concat(ROI_LINE_CHART_STRINGS.xAxisLabel, " ").concat(year);
                    },
                    label: function (context) {
                        var year = Math.round(context.dataIndex / resolution);
                        var label = context.dataset.label || '';
                        var originalValue = actualData[year];
                        return "".concat(label, ": ").concat(formatCurrency(originalValue));
                    },
                },
            },
            legend: {
                labels: {
                    generateLabels: function () { return []; },
                },
            },
            title: {
                display: false,
            },
        },
        hover: {
            mode: 'index',
            intersect: false,
        },
    };
    return (_jsxs(Box, { sx: containerSx, children: [_jsx(ROILineChartHeader, { total_roi: total_roi }), _jsx(Box, { sx: chartContainerSx, children: _jsx(Line, { data: data, options: options }) })] }));
};
var chartContainerSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        width: '100%',
        height: '100%',
        mt: backrs.spacing['spacing-xl'],
    });
};
var containerSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        display: 'flex',
        flexDirection: 'column',
        '@media (max-width: 768px)': {
            width: '100%',
        },
        '@media (max-width: 400px)': {
            width: '100%',
        },
        height: { xs: 'auto', lg: '405px' },
        backgroundColor: backrs.colors['Colors/Base/White'],
        border: "1px solid ".concat(backrs.colors['Colors/Foreground/fg-primary']),
        boxShadow: backrs.shadows['shadow-xs'],
        borderRadius: backrs.radius['radius-xl'],
        padding: {
            xs: backrs.spacing['spacing-xl'],
            sm: backrs.spacing['spacing-2xl'],
        },
    });
};
